// Import React and necessary components
import ProductPrimaryImage, {
    ProductPrimaryImageStyles,
} from "@electricMotorShop/Overrides/Components/ProductPrimaryImage";
import siteMessage from "@insite/client-framework/SiteMessage";
import { ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem/GridItem";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Check from "@insite/mobius/Icons/Check";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

// Extend the Props type to include productTitle
interface OwnProps {
    isQtyAdjusted: boolean;
    multipleProducts: boolean;
    productTitle?: string; // Optional prop for product title
    selectedImage?: ImageModel;
    product: ProductModel;
    onButtonClick: () => void;
}

type Props = OwnProps;

export interface ProductAddedToCartMessageStyles {
    messageWrapper?: InjectableCss;
    defaultText?: TypographyPresentationProps;
    quantityAdjustedText?: TypographyPresentationProps;
    productTitleText?: TypographyPresentationProps; // Style for product title text
    productTitleGridItem?: GridItemProps;
    productImageGridItem?: GridItemProps;
    imageStyles?: ProductPrimaryImageStyles;
    checkIcon?: IconPresentationProps;
    viewCartButton?: ButtonPresentationProps;
    buttonContainerGridItem?: GridItemProps;
}

export const productAddedToCartMessageStyles: ProductAddedToCartMessageStyles = {
    defaultText: {
        css: css`
            font-weight: 700;
            font-size: 20px;
        `,
    },
    quantityAdjustedText: {
        variant: "p",
    },
    productTitleText: {
        css: css`
            font-weight: 700;
            font-size: 14px;
        `,
    },
    productTitleGridItem: {
        width: 7,
    },
    productImageGridItem: {
        width: 5,
        css: css``,
    },
    imageStyles: {
        image: {
            css: css`
                img {
                    width: auto;
                    max-height: 75px;
                }
            `,
        },
        img: {
            css: css`
                width: auto;
                max-height: 75px;
            `,
        },
        centeringWrapper: {
            css: css`
                min-height: 40px;
                display: flex;
                align-items: center;
                justify-content: center;
            `,
        },
    },
    checkIcon: {
        src: Check,
        color: "success.main",
        size: 25,
        css: css`
            margin-right: 10px;
            margin-top: 2px;
        `,
    },
    messageWrapper: {
        css: css`
            max-height: fit-content;
        `,
    },
    viewCartButton: {
        css: css`
            width: 100%;
            margin: 0 auto;
        `,
    },
    buttonContainerGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: center;
            width: 100%;
        `,
    },
};

const styles = productAddedToCartMessageStyles;

// Modify the component to accept and display productTitle
const ProductAddedToCartMessage = ({
    isQtyAdjusted,
    multipleProducts,
    productTitle,
    selectedImage,
    product,
    onButtonClick,
}: Props) => {
    const defaultMessage = multipleProducts
        ? siteMessage("Cart_AllProductsAddedToCart")
        : siteMessage("Cart_ProductAddedToCart");

    const image =
        selectedImage ??
        ({
            id: product.id,
            imageAltText: product.imageAltText,
            imageType: "Static",
            largeImagePath: product.largeImagePath,
            mediumImagePath: product.mediumImagePath,
            smallImagePath: product.smallImagePath,
        } as ImageModel);

    if (!isQtyAdjusted) {
        return <Typography {...styles.defaultText}>{defaultMessage}</Typography>;
    }

    return (
        <GridContainer {...styles.messageWrapper}>
            <GridItem width={12}>
                <IconMemo {...styles.checkIcon} />
                <Typography {...styles.defaultText}>{defaultMessage}</Typography>
            </GridItem>
            <GridItem {...styles.productImageGridItem}>
                {isQtyAdjusted && (
                    <ProductPrimaryImage product={product} image={image} extendedStyles={styles.imageStyles} />
                )}
            </GridItem>
            <GridItem {...styles.productTitleGridItem}>
                {productTitle && <Typography {...styles.productTitleText}>{productTitle}</Typography>}
            </GridItem>
            <GridItem {...styles.buttonContainerGridItem}>
                <Button {...styles.viewCartButton} onClick={onButtonClick}>
                    View Cart & Checkout
                </Button>
            </GridItem>
        </GridContainer>
    );
};

export default ProductAddedToCartMessage;
